/**
 * Todo Scanner Utility
 * Scans /docs/todo/ directory for valid todo lists
 */

export interface TodoMetadata {
  name: string;
  description: string;
  category?: "feature" | "bugfix" | "deployment" | "documentation" | "refactor";
  priority?: "low" | "medium" | "high" | "critical";
  owner?: string;
  createdAt?: string;
  dueDate?: string;
  tags?: string[];
  relatedDocs?: string[];
  phases?: string[];
}

export interface TodoListInfo {
  path: string;
  folderName: string;
  metadata: TodoMetadata;
  hasMetadataFile: boolean;
}

/**
 * Scan the /docs/todo/ directory for valid todo lists
 * A valid todo list is a folder containing a todo.md file
 */
export async function scanTodoDirectory(): Promise<TodoListInfo[]> {
  const todoLists: TodoListInfo[] = [];
  const basePath = "/docs/todo";

  try {
    // IMPORTANT: Browser limitation - cannot scan file system directories
    // New todo folders must be manually added to this array
    //
    // To add a new todo:
    // 1. Create folder in /docs/todo/your-todo-name/
    // 2. Add 'your-todo-name' to the array below
    // 3. Refresh the System Settings page
    //
    // Future enhancement: Create a backend API endpoint to scan directory structure

    const knownTodoFolders = [
      "website-launch",
      // Add new todo folder names here (just the folder name, not the full path)
    ];

    for (const folderName of knownTodoFolders) {
      try {
        // Check if todo.md exists
        const todoPath = `${basePath}/${folderName}/todo.md`;
        const todoResponse = await fetch(todoPath);

        if (!todoResponse.ok) {
          continue; // Skip if todo.md doesn't exist
        }

        // Try to load metadata.json
        let metadata: TodoMetadata;
        let hasMetadataFile = false;

        try {
          const metadataPath = `${basePath}/${folderName}/metadata.json`;
          const metadataResponse = await fetch(metadataPath);

          if (metadataResponse.ok) {
            metadata = await metadataResponse.json();
            hasMetadataFile = true;
          } else {
            // Generate metadata from folder name
            metadata = generateMetadataFromFolderName(folderName);
          }
        } catch {
          // If metadata.json fails to load, generate from folder name
          metadata = generateMetadataFromFolderName(folderName);
        }

        todoLists.push({
          path: todoPath,
          folderName,
          metadata,
          hasMetadataFile,
        });
      } catch (error) {
        console.error(`Error loading todo list from ${folderName}:`, error);
      }
    }

    return todoLists;
  } catch (error) {
    console.error("Error scanning todo directory:", error);
    return [];
  }
}

/**
 * Generate basic metadata from folder name when metadata.json is missing
 */
function generateMetadataFromFolderName(folderName: string): TodoMetadata {
  // Convert kebab-case to Title Case
  const name = folderName
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return {
    name,
    description: `Tasks for ${name.toLowerCase()}`,
    category: "feature",
    priority: "medium",
  };
}

/**
 * Validate that a markdown file contains checkbox tasks
 */
export function validateTodoMarkdown(content: string): boolean {
  // Must have at least one checkbox task
  const checkboxRegex = /^\s*- \[([ xX])\] .+$/m;
  return checkboxRegex.test(content);
}

/**
 * Extract basic stats from todo markdown content
 */
export function getTodoStats(content: string): {
  totalTasks: number;
  completedTasks: number;
  progressPercentage: number;
} {
  const taskRegex = /^\s*- \[([ xX])\] .+$/gm;
  const matches = content.match(taskRegex) || [];
  const totalTasks = matches.length;
  const completedTasks = matches.filter((task) => task.match(/\[x\]/i)).length;
  const progressPercentage =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return {
    totalTasks,
    completedTasks,
    progressPercentage,
  };
}

/**
 * Get a priority color for UI display
 */
export function getPriorityColor(priority?: string): string {
  switch (priority) {
    case "critical":
      return "text-red-600 bg-red-50";
    case "high":
      return "text-orange-600 bg-orange-50";
    case "medium":
      return "text-blue-600 bg-blue-50";
    case "low":
      return "text-slate-600 bg-slate-50";
    default:
      return "text-slate-600 bg-slate-50";
  }
}

/**
 * Get a category icon for UI display
 */
export function getCategoryIcon(category?: string): string {
  switch (category) {
    case "feature":
      return "✨";
    case "bugfix":
      return "🐛";
    case "deployment":
      return "🚀";
    case "documentation":
      return "📚";
    case "refactor":
      return "♻️";
    default:
      return "📋";
  }
}
